// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// eslint-disable-next-line spire/fenced-imports
import { hideAll } from "tippy.js";

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getIsPunchOutSession, getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadCurrentBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadCurrentBillTo";
import loadShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTo";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { mergeWithCss, safeJsonParse, xMask } from "@sc/XDL/Utilities";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";

interface linkGroups {
    title: string;
    links: (
        | {
              title: string;
              url: string;
              type?: string | undefined;
              id: string;
              parentId?: string | undefined;
              excludeFromNavigation?: boolean | undefined;
              children?: PageLinkModel[] | undefined;
              openInNewWindow?: boolean;
          }
        | undefined
    )[];
}

interface MiniSignInProps {
    linkGroups?: linkGroups[];
    onSignOutHandler: () => void;
    extendedStyles?: MyAccountOverlayStyles;
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);

    const { shipToId, billToId } = session;
    const { salespersonInfo } = session.properties;
    const shipToState = getShipToState(state, shipToId);

    return {
        currentBillTo: getCurrentBillToState(state),
        displayChangeCustomerLink: getSession(state).displayChangeCustomerLink,
        changeCustomerUrl: getPageLinkByPageType(state, "ChangeCustomerPage")?.url,
        isPunchOutSession: getIsPunchOutSession(state),
        salespersonInfo: safeJsonParse(salespersonInfo),
        shipToState,
        shipToId,
        billToId,
        address: shipToState.value,
    };
};

type Props = MiniSignInProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface MyAccountOverlayStyles {
    wrapper?: InjectableCss;
    container?: InjectableCss;
    titleWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    linkItems?: {
        wrapper?: InjectableCss;
        linksContainer?: InjectableCss;
        linkWrapper?: InjectableCss;
        title?: TypographyPresentationProps;
        link?: LinkPresentationProps;
    };
    personalInfo?: {
        wrapper?: InjectableCss;
        infoWrapper?: InjectableCss;
        title?: TypographyPresentationProps;
        subTitle?: TypographyPresentationProps;
        infoColumnContainer?: InjectableCss;
        name?: TypographyPresentationProps;
        label?: TypographyPresentationProps;
        accountLink?: LinkPresentationProps;
    };
    actionSection?: {
        wrapper?: InjectableCss;
        signOutButton?: LinkPresentationProps;
        changeShipTo?: LinkPresentationProps;
    };
}

export const myAccountOverlayStyles: MyAccountOverlayStyles = {};

const mapDispatchToProps = {
    loadCurrentBillTo,
    loadShipTo,
};

const MyAccountOverlay: FC<Props> = ({
    shipToState,
    linkGroups,
    onSignOutHandler,
    extendedStyles,
    loadCurrentBillTo,
    isPunchOutSession,
    changeCustomerUrl,
    currentBillTo,
    displayChangeCustomerLink,
    loadShipTo,
    address,
    shipToId,
    billToId,
    salespersonInfo,
}) => {
    const [styles] = React.useState(() => mergeWithCss(myAccountOverlayStyles, extendedStyles));
    const history = useHistory();

    useEffect(() => {
        if (!currentBillTo.value && !currentBillTo.isLoading) {
            setTimeout(() => {
                loadCurrentBillTo();
            }, 500);
        }
    }, []);
    useEffect(() => {
        if (!shipToState.value && !shipToState.isLoading && shipToId && billToId) {
            loadShipTo({ shipToId, billToId });
        }
    }, []);

    const linksSection = linkGroups?.map((linkGroup, index) => {
        if (linkGroup.links.length > 0) {
            return (
                // eslint-disable-next-line react/no-array-index-key
                <StyledWrapper {...styles.linkItems?.wrapper} key={index}>
                    {linkGroup.title && <Typography {...styles.linkItems?.title}>{linkGroup.title}</Typography>}
                    <StyledWrapper {...styles.linkItems?.linksContainer}>
                        {linkGroup.links.map(
                            link =>
                                link?.url && (
                                    <StyledWrapper
                                        key={`${link?.title}_${link?.url}`}
                                        {...styles.linkItems?.linkWrapper}
                                    >
                                        <Link
                                            onClick={() => {
                                                hideAll();
                                            }}
                                            href={link?.url}
                                            {...styles.linkItems?.link}
                                            target={(link as any).openInNewWindow === true ? "_blank" : ""}
                                            aria-label={link?.title}
                                        >
                                            {link?.title}
                                        </Link>
                                    </StyledWrapper>
                                ),
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            );
        }
        return null;
    });

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.container}>
                <StyledWrapper {...styles.titleWrapper}>
                    <Typography {...styles.title}>
                        <b>
                            {translate("Bill-To #")}
                            {": "}
                        </b>
                        <Link
                            onClick={() => {
                                changeCustomerUrl && history.push(changeCustomerUrl);
                                hideAll();
                            }}
                            aria-label="Change bill-To"
                            {...styles.actionSection?.changeShipTo}
                            data-test-selector="header-changeCustomer"
                        >
                            {currentBillTo?.value?.customerNumber}
                        </Link>
                    </Typography>
                    <Typography {...styles.title}>
                        <b>
                            {translate("Ship-To #")}
                            {": "}
                        </b>
                        <Link
                            onClick={() => {
                                changeCustomerUrl && history.push(changeCustomerUrl);
                                hideAll();
                            }}
                            aria-label="Change Ship-To"
                            {...styles.actionSection?.changeShipTo}
                            data-test-selector="header-changeCustomer"
                        >
                            {shipToState.value?.customerSequence || currentBillTo?.value?.customerNumber}
                        </Link>
                    </Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.personalInfo?.wrapper}>
                    <Typography {...styles.personalInfo?.title}>{translate("Account Information")}</Typography>
                    <StyledWrapper {...styles.personalInfo?.infoWrapper}>
                        {salespersonInfo && (
                            <StyledWrapper {...styles.personalInfo?.infoColumnContainer}>
                                <Typography {...styles.personalInfo?.subTitle}>{translate("Sales Rep")}</Typography>
                                <Typography {...styles.personalInfo?.name}>{salespersonInfo.name}</Typography>
                                {salespersonInfo.email && (
                                    <Link
                                        href={`mailto:${salespersonInfo.email}`}
                                        onClick={() => {
                                            hideAll();
                                        }}
                                        aria-label={salespersonInfo.email}
                                        {...styles.personalInfo?.accountLink}
                                    >
                                        {salespersonInfo.email}
                                    </Link>
                                )}
                                {salespersonInfo.phone1 && (
                                    <Link
                                        href={`tel:${salespersonInfo.phone1}`}
                                        onClick={() => {
                                            hideAll();
                                        }}
                                        {...styles.personalInfo?.accountLink}
                                        aria-label={salespersonInfo.phone1}
                                    >
                                        {xMask.addressPhone(salespersonInfo.phone1)}
                                    </Link>
                                )}
                            </StyledWrapper>
                        )}

                        <StyledWrapper {...styles.personalInfo?.infoColumnContainer}>
                            <Typography {...styles.personalInfo?.subTitle}>{translate("Shipping Address")}</Typography>
                            {[address?.address1, address?.address2].map(address =>
                                address ? (
                                    <Typography {...styles.personalInfo?.label} key={address}>
                                        {address}
                                    </Typography>
                                ) : null,
                            )}
                            <Typography {...styles.personalInfo?.label}>
                                {address?.city},{" "}
                                {typeof address?.state === "string" ? address?.state : address?.state?.abbreviation}{" "}
                                {address?.postalCode}
                            </Typography>
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
                {linksSection}
            </StyledWrapper>
            <StyledWrapper {...styles.actionSection?.wrapper}>
                <Link
                    {...styles.actionSection?.signOutButton}
                    onClick={() => {
                        onSignOutHandler?.();
                        hideAll();
                    }}
                    aria-label="Log Out"
                    data-test-selector="header-logOutLink"
                >
                    {isPunchOutSession ? translate("Cancel PunchOut") : translate("Log Out")}
                </Link>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(MyAccountOverlay);
